import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Loader2 } from "lucide-react";
import { fetchClinics, Clinic } from "@/services/clinic-service";
import { fetchAppointments, AppointmentData } from "@/services/appointment-service";
import { sendRebookingInvite } from "@/services/public-scheduling-service";
import { statusLabels } from "@/utils/appointmentStatus";
import { formatISODate } from "@/utils/formatters";
import { AppointmentsTable } from "@/components/appointments/AppointmentsTable";
import { WizardAgendamento } from "@/components/WizardAgendamento/WizardAgendamento";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send } from "lucide-react";
import { toast } from "sonner";

export function PatientAppointmentsTab({ patientId }: { patientId: string }) {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<string>("20");

  const now = new Date();
  const futureDate = new Date();
  futureDate.setDate(now.getDate() + 30);
  const pastDate = new Date();
  pastDate.setDate(now.getDate() - 90);

  const [clinicId, setClinicId] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>(formatISODate(pastDate));
  const [endDate, setEndDate] = useState<string>(formatISODate(futureDate));

  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentData | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [originalAppointmentId, setOriginalAppointmentId] = useState<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isRebookingInviteOpen, setIsRebookingInviteOpen] = useState(false);
  const [isSendingRebookingInvite, setIsSendingRebookingInvite] = useState(false);

  const handleOpenRebookingInviteDialog = (appointment: AppointmentData) => {
    setSelectedAppointment(appointment);
    setIsRebookingInviteOpen(true);
  };

  const handleSendRebookingInvite = async () => {
    if (!selectedAppointment) return;
    setIsSendingRebookingInvite(true);
    try {
      await sendRebookingInvite({
        clinic_id: selectedAppointment.clinic_id,
        cellphone: selectedAppointment.patient_cellphone,
        appointment_id: selectedAppointment.id,
        specialty_id: selectedAppointment.dental_specialty_id,
      });
      toast.success(`Convite de reagendamento enviado para ${selectedAppointment.patient_name}.`);
      setIsRebookingInviteOpen(false);
    } catch (error: any) {
      toast.error(error.message || "Não foi possível enviar o convite.");
    } finally {
      setIsSendingRebookingInvite(false);
    }
  };

  const handleRebooking = (appointment: AppointmentData) => {
    const patientData = {
      id: appointment.patient_id,
      name: appointment.patient_name,
      cpf: appointment.patient_cpf,
      cellphone: appointment.patient_cellphone,
      dental_insurance_id: appointment.patient_dental_insurance_id || appointment.dental_insurance_id,
      card_number: appointment.patient_card_number || appointment.card_number,
    };
    setSelectedPatient(patientData);
    setOriginalAppointmentId(appointment.id);
    setIsWizardOpen(true);
  };

  useEffect(() => {
    fetchClinics().then(setClinics);
    handleFilter();
  }, [patientId, currentPage, pageSize]);

  const handleFilter = async () => {
    try {
      setLoading(true);
      const data = await fetchAppointments(
        true, false,
        clinicId === "all" ? undefined : clinicId,
        patientId,
        undefined, undefined, undefined,
        startDate, endDate,
        status === "all" ? undefined : status,
        undefined, undefined, undefined, undefined,
        currentPage,
        parseInt(pageSize)
      );
      setAppointments(data);
    } catch (error) {
      toast.error("Erro ao carregar atendimentos");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setClinicId("all");
    setStatus("all");
    setStartDate(formatISODate(pastDate));
    setEndDate(formatISODate(futureDate));
    setPageSize("20");
    setCurrentPage(1);
  };

  return (
    <div className="space-y-2">
      <div className="filter-box">
        <div className="flex items-end gap-2 w-full flex-wrap">
          <div className="flex-1 min-w-[120px] space-y-1">
            <Label className="filter-label">Clínica</Label>
            <Select value={clinicId} onValueChange={setClinicId}>
              <SelectTrigger className="filter-input">
                <SelectValue placeholder="Clínica" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as clínicas</SelectItem>
                {clinics.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-[120px] space-y-1">
            <Label className="filter-label">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="filter-input">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {Object.entries(statusLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-32 space-y-1">
            <Label className="filter-label">Início</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="filter-input" />
          </div>

          <div className="w-32 space-y-1">
            <Label className="filter-label">Fim</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="filter-input" />
          </div>

          <div className="w-36 space-y-1">
            <Label className="filter-label">Registros por Página</Label>
            <Select value={pageSize} onValueChange={(val) => { setPageSize(val); setCurrentPage(1); }}>
              <SelectTrigger className="filter-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 por página</SelectItem>
                <SelectItem value="20">20 por página</SelectItem>
                <SelectItem value="50">50 por página</SelectItem>
                <SelectItem value="100">100 por página</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={() => { setCurrentPage(1); handleFilter(); }} className="btn-header gap-1.5">
              <Search className="btn-header-icon" /> Filtrar
            </Button>
            <Button size="sm" variant="outline" onClick={handleClear} className="btn-header">
              Limpar
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground uppercase tracking-tight">Buscando histórico...</p>
        </div>
      ) : (
        <>
          <AppointmentsTable
            appointments={appointments}
            onSendRebookingInvite={handleOpenRebookingInviteDialog}
            onRebooking={handleRebooking}
            hideClinic={false}
            hideDentist={false}
            hideCellphone={true}
            hideCpf={true}
          />

          {appointments.length > 0 && (
            <div className="flex items-center justify-between border rounded-lg px-4 py-2 bg-card">
              <span className="page-counter">Página {currentPage} — {appointments.length} registros</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="btn-header"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" className="btn-header"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={appointments.length < parseInt(pageSize)}>
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {isWizardOpen && selectedPatient && (
        <WizardAgendamento
          patient={selectedPatient}
          originalAppointmentId={originalAppointmentId || undefined}
          onClose={() => { setIsWizardOpen(false); setSelectedPatient(null); }}
          onSuccess={() => { handleFilter(); }}
        />
      )}

      <AlertDialog open={isRebookingInviteOpen} onOpenChange={setIsRebookingInviteOpen}>
        <AlertDialogContent className="max-w-[420px]">
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <Send className="h-5 w-5" />
              <span className="font-bold uppercase tracking-wider text-xs">WhatsApp Business</span>
            </div>
            <AlertDialogTitle>Enviar convite de reagendamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Será enviada uma mensagem para <strong>{selectedAppointment?.patient_name}</strong> perguntando se deseja escolher um novo horário pela conversa no WhatsApp.
              <br /><br />
              <span className="text-xs text-muted-foreground">
                Consulta atual: {selectedAppointment?.dental_specialty_name} · {selectedAppointment?.slot_date} {selectedAppointment?.slot_time}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSendingRebookingInvite}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={(e) => { e.preventDefault(); handleSendRebookingInvite(); }}
              disabled={isSendingRebookingInvite}
            >
              {isSendingRebookingInvite ? "Enviando..." : "Sim, enviar agora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
